import { supabase } from "@/services/supabase/createClient";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";

const subscriptionTypeLevels = ["free", "pro"];

export const getTeams = async () => {
  const { data, error } = await supabase.rpc("get_user_teams");

  if (error) {
    Sentry.captureException(error);
    throw new Error(error.message);
  }

  // this request might return the same team multiple times if the owner has multiple subscriptions
  // we dedup teams and keep the highest owner_subscription_type only
  const dedupedData = data?.reduce((acc: any, team: any) => {
    if (acc[team.team_id]) {
      if (
        subscriptionTypeLevels.indexOf(team.owner_subscription_type) >
        subscriptionTypeLevels.indexOf(acc[team.team_id].owner_subscription_type)
      ) {
        acc[team.team_id] = team;
      }
    } else {
      acc[team.team_id] = team;
    }
    return acc;
  }, {});

  return Object.values(dedupedData)?.map((team: any) => ({
    id: team.team_id,
    name: team.team_name,
    role: team.user_role,
    subscriptionType: team.owner_subscription_type,
    subscriptionId: team.owner_subscription_id,
  }));
};

export const useTeams = () => {
  return useQuery({
    queryKey: ["teams"],
    queryFn: () => getTeams(),
  });
};

export const useTeamSubscription = () => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["teams_subscription"],
    queryFn: () => {
      const channels = supabase
        .channel("teams_all")
        .on("postgres_changes", { event: "*", schema: "public", table: "teams" }, () => {
          queryClient.invalidateQueries({
            predicate: (query) => query.queryKey?.[0] === "teams",
          });
        })
        .subscribe();
      return () => {
        channels.unsubscribe();
      };
    },
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};
